import React from "react";
import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { Checkbox, Tooltip } from "@narmi/design_system";
import { ContextForm } from "cerulean";
import Feature, { featureEnabled } from "byzantine/src/Feature";
import { MODEL_STR } from "byzantine/src/Role";
import RoleExternalAccountsField from "./RoleExternalAccountsField";
import styles from "./OrganizationRoles.module.scss";
import {
  RoleFormData,
  allExternalAccountsSelected,
  canMakeExternalTransfers,
  validateExternalAccounts,
} from "./RoleFormUtils";

interface RoleLevelPermissionsSectionProps {
  features?: Record<string, any>;
  formData: Record<string, any>;
  isUsingConsumerBillpay?: boolean;
}

/**
 * Permissions that are not tied to a single account, plus approvals. Both write the same flat
 * `formData[model_str]` booleans the rest of the form already uses.
 */
export default function RoleLevelPermissionsSection({
  features = {},
  formData,
  isUsingConsumerBillpay = false,
}: RoleLevelPermissionsSectionProps) {
  const { l10n } = useLocalization();
  const businessBillpayEnabled =
    featureEnabled(features, { or: ["bill_pay"] }) && !isUsingConsumerBillpay;

  const allAccountsSelected = Boolean(formData[MODEL_STR.ACCOUNT]);
  const canTransfer = canMakeExternalTransfers(formData as RoleFormData);

  // Nested rows (e.g. BillPay recipients) live inside their parent's row so the divider only
  // separates top-level permissions. A permission whose requirement isn't met stays visible but
  // disabled (`isDisabled`).
  const row = (
    field: string,
    label: string,
    {
      note,
      tooltip,
      nested,
      isDisabled = false,
    }: {
      note?: string;
      tooltip?: string;
      nested?: React.ReactNode;
      isDisabled?: boolean;
    } = {}
  ) => (
    <div className={styles.checkRow} key={field}>
      {/* The tooltip sits beside the checkbox, not in its label, so clicking it doesn't toggle. */}
      <div className="alignChild--left--center">
        <ContextForm.Field style={{ marginBottom: "0px" }}>
          <Checkbox {...{ field }} label={label} isDisabled={isDisabled} />
        </ContextForm.Field>
        {tooltip && (
          <span className={styles.accessInfo}>
            <Tooltip text={tooltip}>
              <span className="narmi-icon-info" />
            </Tooltip>
          </span>
        )}
      </div>
      {note && <p className={styles.rowNote}>{note}</p>}
      {nested}
    </div>
  );

  return (
    <>
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          {l10n.getString("org-role-section-role-level")}
        </h4>
        <p className={styles.sectionDescription}>
          {l10n.getString("org-role-section-role-level-description")}
        </p>
        <div className={styles.card}>
          {row(MODEL_STR.RECIPIENT, l10n.getString("manage-users-recipient"))}
          {/* Card management and statements are not scoped to selected accounts yet, so they are
              disabled until all accounts are selected (same prerequisite as the legacy checklist). */}
          <Feature features={features} flag="cards">
            {row(MODEL_STR.CARD, l10n.getString("manage-users-card"), {
              isDisabled: !allAccountsSelected,
            })}
          </Feature>
          {businessBillpayEnabled &&
            row(
              MODEL_STR.PAYMENT,
              l10n.getString("org-role-new-perm-billpay"),
              {
                tooltip: l10n.getString(
                  "org-role-perm-tooltip-move-money-all-accounts"
                ),
                nested: (
                  <div className={styles.nestedCheckRow}>
                    <ContextForm.Field style={{ marginBottom: "0px" }}>
                      <Checkbox
                        {...{ field: MODEL_STR.PAYEE }}
                        label={l10n.getString(
                          "org-role-new-perm-billpay-recipients"
                        )}
                        isDisabled={!formData[MODEL_STR.PAYMENT]}
                      />
                    </ContextForm.Field>
                  </div>
                ),
              }
            )}
          <Feature features={features} flag="ach">
            <>
              {canTransfer && formData.externalAccounts.length > 0 && (
                <div className={styles.checkRow}>
                  <ContextForm.Field
                    validate={(
                      value: { selected?: boolean }[],
                      data: Record<string, any>
                    ) => validateExternalAccounts(value, data as RoleFormData)}
                    style={{ marginBottom: "0px" }}
                  >
                    <RoleExternalAccountsField field="externalAccounts" />
                  </ContextForm.Field>
                </div>
              )}
              {/* Saves as a grant for every external account, so only enabled once all are selected. */}
              {row(
                MODEL_STR.EXTERNAL_ACCOUNT,
                l10n.getString("manage-users-external-account"),
                {
                  tooltip: l10n.getString(
                    "org-role-perm-tooltip-external-accounts"
                  ),
                  isDisabled:
                    !canTransfer ||
                    !allExternalAccountsSelected(formData as RoleFormData),
                }
              )}
            </>
          </Feature>
          <Feature features={features} flag="zelle">
            {row(MODEL_STR.ZELLE, l10n.getString("manage-users-zelle"), {
              tooltip: l10n.getString(
                "org-role-perm-tooltip-move-money-all-accounts"
              ),
            })}
          </Feature>
          <Feature features={features} flag="statements">
            {row(MODEL_STR.DOCUMENTS, l10n.getString("manage-users-documents"), {
              tooltip: l10n.getString("org-role-perm-tooltip-view-all-accounts"),
              isDisabled: !allAccountsSelected,
            })}
          </Feature>
          <Feature features={features} flag="ach_file_import">
            {row(
              MODEL_STR.ACH_IMPORT_FILE_DOWNLOADS,
              l10n.getString("manage-users-ach-import-file-downloads")
            )}
          </Feature>
          <Feature features={features} flag="business_banking_api">
            {row(
              MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES,
              l10n.getString("org-role-perm-create-api-tokens")
            )}
          </Feature>
        </div>
        <p className={cc(["nds-subheader", "margin--top--l"])}>
          {l10n.getString("org-role-section-approvals")}
        </p>
        <div className={styles.card}>
          {row(
            MODEL_STR.DUAL_APPROVAL_REQUEST,
            l10n.getString("manage-users-dual-approval-request"),
            { note: l10n.getString("org-role-approvals-dual-approval-note") }
          )}
          <Feature features={features} flag="ach_templates">
            {row(
              MODEL_STR.APPROVE_ACH_TEMPLATES,
              l10n.getString("manage-users-ach-instant-approve-templates")
            )}
          </Feature>
        </div>
      </section>
    </>
  );
}
